import { useMemo, useState, type ReactElement } from 'react';
import { Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';

import { getPatientSummaryFullName } from '@/api/mappers';
import { Button } from '@/components/ui/button';
import { PageWithSidebar } from '@/components/shell/PageWithSidebar';
import { BrowseNavSidebar } from '@/components/shell/BrowseNavSidebar';
import { BrowsePatientsPanel } from '@/features/patient-search/components/BrowsePatientsPanel';
import { BrowsePatientsSkeleton } from '@/features/patient-search/components/BrowsePatientsSkeleton';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { useMe } from '@/hooks/useMe';
import {
  RECENT_PATIENTS_DEFAULT_LIMIT,
  RECENT_PATIENTS_MAX_LIMIT,
  RECENT_PATIENTS_PAGE_SIZE,
  useRecentPatients,
} from '@/hooks/useRecentPatients';
import {
  nextPatientSort,
  parsePatientSortKey,
  parseSortDirection,
  sortPatientSummaries,
  type PatientSortKey,
} from '@/features/patient-search/lib/sortPatientSummaries';
import type { PatientSummaryViewModel } from '@/models/patient';

export function BrowsePatientsPage(): ReactElement {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [limit, setLimit] = useState(RECENT_PATIENTS_DEFAULT_LIMIT);
  const query = useRecentPatients({ limit });
  const { data: me } = useMe();
  const isLoadingMore = query.isFetching && !query.isPending;
  // The endpoint exposes only `limit` (no offset/cursor), so we infer
  // "there might be more" from a full page once a fetch settles: fewer rows
  // than asked for means we've hit the end, and the endpoint rejects `limit`
  // above RECENT_PATIENTS_MAX_LIMIT so we stop at the ceiling too.
  const canLoadMore =
    limit < RECENT_PATIENTS_MAX_LIMIT && (query.data?.length ?? 0) >= limit;
  // Keep the button mounted for the whole in-flight fetch — including the
  // final bump to the ceiling — so the spinner shows. Applying the ceiling
  // check only when idle avoids unmounting mid-load (`keepPreviousData`
  // means `data` still holds the pre-bump rows while fetching).
  const showLoadMore = isLoadingMore || canLoadMore;
  const loadMore = (): void => {
    setLimit((current) =>
      Math.min(current + RECENT_PATIENTS_PAGE_SIZE, RECENT_PATIENTS_MAX_LIMIT),
    );
  };

  const q = searchParams.get('q') ?? '';
  const program = searchParams.get('program') ?? 'all';
  const gc = searchParams.get('gc') ?? 'all';
  const sortKey = parsePatientSortKey(searchParams.get('sort'));
  const sortDir = parseSortDirection(searchParams.get('dir')) ?? 'asc';

  const isMineRoute =
    pathname === '/browse/patients/mine' || pathname.endsWith('/patients/mine');

  function setQ(val: string): void {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (val.length > 0) {
          next.set('q', val);
        } else {
          next.delete('q');
        }
        return next;
      },
      { replace: true },
    );
  }

  function setProgram(val: string): void {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (val !== 'all') {
          next.set('program', val);
        } else {
          next.delete('program');
        }
        return next;
      },
      { replace: true },
    );
  }

  function setGc(val: string): void {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (val !== 'all') {
          next.set('gc', val);
        } else {
          next.delete('gc');
        }
        return next;
      },
      { replace: true },
    );
  }

  function onSortChange(key: PatientSortKey): void {
    const next = nextPatientSort({ key: sortKey, direction: sortDir }, key);
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next.key === null) {
          params.delete('sort');
          params.delete('dir');
        } else {
          params.set('sort', next.key);
          params.set('dir', next.direction);
        }
        return params;
      },
      { replace: true },
    );
  }

  const rows = useMemo(
    () => (query.isSuccess ? query.data : []),
    [query.isSuccess, query.data],
  );

  const { programs, gcs } = useMemo(() => {
    const ps = new Set<string>();
    const gs = new Set<string>();
    for (const p of rows) {
      if (p.program !== null && p.program.length > 0) ps.add(p.program);
      if (p.assignedGc !== null && p.assignedGc.length > 0) gs.add(p.assignedGc);
    }
    return {
      programs: [...ps].sort((a, b) => a.localeCompare(b)),
      gcs: [...gs].sort((a, b) => a.localeCompare(b)),
    };
  }, [rows]);

  const filtered = useMemo(() => {
    let list: readonly PatientSummaryViewModel[] = rows;
    if (isMineRoute && me !== undefined) {
      list = list.filter((p) => p.assignedGc === me.id);
    }
    const s = q.trim().toLowerCase();
    if (s.length > 0) {
      list = list.filter((p) => {
        const n = (getPatientSummaryFullName(p) ?? '').toLowerCase();
        return n.includes(s);
      });
    }
    if (program !== 'all') {
      list = list.filter((p) => p.program === program);
    }
    if (gc !== 'all') {
      list = list.filter((p) => p.assignedGc === gc);
    }
    return list;
  }, [rows, isMineRoute, me, q, program, gc]);

  const sorted = useMemo(
    () => sortPatientSummaries(filtered, sortKey, sortDir),
    [filtered, sortKey, sortDir],
  );

  return (
    <PageWithSidebar
      sidebar={<BrowseNavSidebar showPatientFilterGroup />}
    >
      <div className="sticky top-0 z-20 border-b border-border bg-card px-8 py-3 flex items-center gap-3">
        <Users
          className="h-[18px] w-[18px] text-muted-foreground shrink-0"
          aria-hidden="true"
        />
        <h1 className="text-[15px] font-semibold tracking-tight text-foreground">
          {t('routes.browsePatients.heading')}
        </h1>
        <div className="ml-auto flex items-center gap-3">
          {query.isSuccess && (
            <>
              <span className="text-[12px] text-muted-foreground tabular-nums">
                {filtered.length === rows.length
                  ? `${rows.length.toLocaleString()}${canLoadMore ? '+' : ''} patients`
                  : `${filtered.length.toLocaleString()} of ${rows.length.toLocaleString()}${canLoadMore ? '+' : ''}`}
              </span>
              {showLoadMore && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadMore}
                  loading={isLoadingMore}
                  loadingText={t('routes.browsePatients.loadingMore')}
                  className="h-7 text-xs"
                >
                  {t('routes.browsePatients.loadMore')}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="px-8 py-5 space-y-4 bg-background">
        {query.isPending && <BrowsePatientsSkeleton />}

        {query.isError && (
          <div className="max-w-xl">
            <FetchErrorCallout
              error={query.error}
              heading={t('routes.browsePatients.error.heading')}
              message={t('routes.browsePatients.error.message')}
              codeLabel={t('routes.browsePatients.error.codeLabel')}
              onRetry={() => {
                query.refetch().catch(/* noop */() => { /* intentional */ });
              }}
              retryLabel={t('routes.browsePatients.error.retry')}
            />
          </div>
        )}

        {query.isSuccess && (
          <BrowsePatientsPanel
            programs={programs}
            gcs={gcs}
            q={q}
            onQueryChange={setQ}
            program={program}
            onProgramChange={setProgram}
            gc={gc}
            onGcChange={setGc}
            patients={sorted}
            isMineRoute={isMineRoute}
            sortKey={sortKey}
            sortDir={sortDir}
            onSortChange={onSortChange}
          />
        )}
      </div>
    </PageWithSidebar>
  );
}
